import React, { useState } from "react";
import { Checkbox, Typography } from "@mui/material";

import {
  useDBTInterruptStore,
  useDBTInterruptActions,
} from "../../../../../store/useDBTInterruptStore";
import {
  ButtonsContainer,
  CancelButton,
  FieldsContainer,
  FormContainer,
  FormTitle,
  StyledCard,
  StyledCardContent,
  StyledTextField,
  StyledFormControlLabel,
  SourcesContainer,
  SubmitButton,
} from "./style";
import useChatStream from "../../../ChatBot/hooks/useChatStream";
import {
  useSessionActions,
  useSessionStore,
} from "../../../../../store/useSessionStore";

type Props = {};

interface FormData {
  query_description: string;
  invoke_dbt_agent: boolean;
}

interface FormErrors {
  query_description?: string;
  invoke_dbt_agent?: string;
}

const QuerySelectionForm = (props: Props) => {
  const { streamHITLHandler } = useChatStream();
  const threadId = useSessionStore.use.thread();

  const { setInterruptAction } = useSessionActions();
  const { setDBTQuerySelectionConfigValues } = useDBTInterruptActions();
  const querySelectionConfig =
    useDBTInterruptStore.use.query_selection_config();
  const dbtSources = querySelectionConfig?.additional_kwargs?.dbt_sources;

  // Local form state
  const [formData, setFormData] = useState<FormData>({
    query_description: "",
    invoke_dbt_agent: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation functions
  const validateQueryDescription = (
    description: string
  ): string | undefined => {
    if (!description.trim()) {
      return "Query description is required";
    }

    if (description.length < 10) {
      return "Query description must be at least 10 characters";
    }

    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Only validate query description if DBT agent is invoked
    if (formData.invoke_dbt_agent) {
      const descriptionError = validateQueryDescription(
        formData.query_description
      );
      if (descriptionError) newErrors.query_description = descriptionError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        field === "invoke_dbt_agent"
          ? event.target.checked
          : event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Map form fields to store structure
      const configData = {
        query_description: formData.query_description,
        invoke_dbt_agent: formData.invoke_dbt_agent,
      };

      // Push to store
      setDBTQuerySelectionConfigValues(configData);

      // Handle form submission logic
      console.log("DBT Query Selection Form submitted:", configData);

      await streamHITLHandler(threadId, configData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    // Reset form on cancel
    setFormData({
      query_description: "",
      invoke_dbt_agent: false,
    });
    setIsSubmitting(true);

    try {
      // Map form fields to store structure
      const configData = {
        query_description: "",
        invoke_dbt_agent: false,
      };

      // Push to store
      setDBTQuerySelectionConfigValues(configData);

      // Handle form submission logic
      console.log("DBT Query Cancel Selection Form submitted:", configData);

      await streamHITLHandler(threadId, configData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.invoke_dbt_agent
    ? formData.query_description.trim() && Object.keys(errors).length === 0
    : Object.keys(errors).length === 0;

  if (isSubmitting) {
    return null;
  }

  return (
    <FormContainer>
      <StyledCard>
        <FormTitle variant="h6">DBT Query Selection</FormTitle>
        <StyledCardContent>
          <FieldsContainer>
            <StyledFormControlLabel
              control={
                <Checkbox
                  checked={formData.invoke_dbt_agent}
                  onChange={handleInputChange("invoke_dbt_agent")}
                  color="primary"
                />
              }
              label="Invoke DBT Agent"
            />

            {formData.invoke_dbt_agent && (
              <>
                {dbtSources && (
                  <div>
                    <Typography variant="subtitle2" gutterBottom>
                      sources.yaml
                    </Typography>
                    <SourcesContainer>
                      <pre>{dbtSources}</pre>
                    </SourcesContainer>
                  </div>
                )}

                <StyledTextField
                  fullWidth
                  label="Query Description"
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  value={formData.query_description}
                  onChange={handleInputChange("query_description")}
                  error={!!errors.query_description}
                  helperText={errors.query_description}
                  placeholder="Describe the query or analysis you want to perform..."
                  hasError={!!errors.query_description}
                />
              </>
            )}
          </FieldsContainer>

          <ButtonsContainer>
            <CancelButton
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </CancelButton>
            <SubmitButton
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </SubmitButton>
          </ButtonsContainer>
        </StyledCardContent>
      </StyledCard>
    </FormContainer>
  );
};

export default QuerySelectionForm;
